import { createApi } from "@reduxjs/toolkit/query/react";
import { baseApiQuery } from "../../../shared/api";
import { BaseResponse } from "../../../shared/types";
import {
  AdminInvestmentApplicationResponse,
  AdminInvestmentApplicationsResponse,
  AdminInvestmentResponse,
  AdminInvestmentsResponse,
  ExternalInvestmentBody,
  GetAdminInvestmentApplicationsParams,
  GetAdminInvestmentsParams,
  InvestmentFileUploadResponse,
  RejectionReasonBody,
} from "./types";

const buildApplicationsQuery = (params: GetAdminInvestmentApplicationsParams) => {
  const qp = new URLSearchParams();
  if (params.status) qp.append("status", params.status);
  if (params.pageToken) qp.append("pageToken", params.pageToken);
  if (params.limit != null) qp.append("limit", params.limit.toString());
  const query = qp.toString();
  return `/admin/investments/applications${query ? `?${query}` : ""}`;
};

const buildInvestmentsQuery = (params: GetAdminInvestmentsParams) => {
  const qp = new URLSearchParams();
  if (params.status) qp.append("status", params.status);
  if (params.external !== undefined) qp.append("external", String(params.external));
  if (params.isHidden !== undefined) qp.append("isHidden", String(params.isHidden));
  if (params.visibleInPublicArchive !== undefined) {
    qp.append("visibleInPublicArchive", String(params.visibleInPublicArchive));
  }
  if (params.search) qp.append("search", params.search);
  if (params.sort) qp.append("sort", params.sort);
  if (params.order) qp.append("order", params.order);
  if (params.pageToken) qp.append("pageToken", params.pageToken);
  if (params.limit != null) qp.append("limit", params.limit.toString());
  const query = qp.toString();
  return `/admin/investments${query ? `?${query}` : ""}`;
};

export const investmentsApi = createApi({
  reducerPath: "investmentsApi",
  baseQuery: baseApiQuery,
  tagTypes: [
    "InvestmentApplications",
    "InvestmentApplication",
    "Investments",
    "Investment",
  ],
  endpoints: (builder) => ({
    getAdminInvestmentApplications: builder.query<
      AdminInvestmentApplicationsResponse,
      GetAdminInvestmentApplicationsParams
    >({
      query: (params) => buildApplicationsQuery(params),
      providesTags: ["InvestmentApplications"],
    }),

    getAdminInvestmentApplication: builder.query<
      AdminInvestmentApplicationResponse,
      number
    >({
      query: (id) => `/admin/investments/applications/${id}`,
      providesTags: (_result, _error, id) => [{ type: "InvestmentApplication", id }],
    }),

    approveInvestmentApplication: builder.mutation<AdminInvestmentResponse, number>({
      query: (id) => ({
        url: `/admin/investments/applications/${id}/approve`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, id) => [
        "InvestmentApplications",
        "Investments",
        { type: "InvestmentApplication", id },
      ],
    }),

    rejectInvestmentApplication: builder.mutation<
      BaseResponse,
      { id: number; body: RejectionReasonBody }
    >({
      query: ({ id, body }) => ({
        url: `/admin/investments/applications/${id}/reject`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        "InvestmentApplications",
        { type: "InvestmentApplication", id },
      ],
    }),

    needsRevisionInvestmentApplication: builder.mutation<
      BaseResponse,
      { id: number; body: RejectionReasonBody }
    >({
      query: ({ id, body }) => ({
        url: `/admin/investments/applications/${id}/needs-revision`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        "InvestmentApplications",
        { type: "InvestmentApplication", id },
      ],
    }),

    getAdminInvestments: builder.query<AdminInvestmentsResponse, GetAdminInvestmentsParams>({
      query: (params) => buildInvestmentsQuery(params),
      providesTags: ["Investments"],
    }),

    getAdminInvestment: builder.query<AdminInvestmentResponse, number>({
      query: (id) => `/admin/investments/${id}`,
      providesTags: (_result, _error, id) => [{ type: "Investment", id }],
    }),

    createExternalInvestment: builder.mutation<AdminInvestmentResponse, ExternalInvestmentBody>({
      query: (body) => ({
        url: "/admin/investments",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Investments"],
    }),

    updateExternalInvestment: builder.mutation<
      AdminInvestmentResponse,
      { id: number; body: ExternalInvestmentBody }
    >({
      query: ({ id, body }) => ({
        url: `/admin/investments/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        "Investments",
        { type: "Investment", id },
      ],
    }),

    uploadInvestmentLogo: builder.mutation<InvestmentFileUploadResponse, FormData>({
      query: (formData) => ({
        url: "/admin/investments/logo",
        method: "POST",
        body: formData,
      }),
    }),

    uploadInvestmentPitchDeck: builder.mutation<InvestmentFileUploadResponse, FormData>({
      query: (formData) => ({
        url: "/admin/investments/pitch-deck",
        method: "POST",
        body: formData,
      }),
    }),

    completeInvestment: builder.mutation<AdminInvestmentResponse, number>({
      query: (id) => ({
        url: `/admin/investments/${id}/complete`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, id) => [
        "Investments",
        { type: "Investment", id },
      ],
    }),

    removeInvestment: builder.mutation<AdminInvestmentResponse, number>({
      query: (id) => ({
        url: `/admin/investments/${id}/remove`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, id) => [
        "Investments",
        { type: "Investment", id },
      ],
    }),

    restoreInvestment: builder.mutation<AdminInvestmentResponse, number>({
      query: (id) => ({
        url: `/admin/investments/${id}/restore`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, id) => [
        "Investments",
        { type: "Investment", id },
      ],
    }),

    hideInvestment: builder.mutation<AdminInvestmentResponse, number>({
      query: (id) => ({
        url: `/admin/investments/${id}/hide`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, id) => [
        "Investments",
        { type: "Investment", id },
      ],
    }),

    unhideInvestment: builder.mutation<AdminInvestmentResponse, number>({
      query: (id) => ({
        url: `/admin/investments/${id}/unhide`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, id) => [
        "Investments",
        { type: "Investment", id },
      ],
    }),
  }),
});

export const {
  useGetAdminInvestmentApplicationsQuery,
  useGetAdminInvestmentApplicationQuery,
  useLazyGetAdminInvestmentApplicationQuery,
  useApproveInvestmentApplicationMutation,
  useRejectInvestmentApplicationMutation,
  useNeedsRevisionInvestmentApplicationMutation,
  useGetAdminInvestmentsQuery,
  useGetAdminInvestmentQuery,
  useLazyGetAdminInvestmentQuery,
  useCreateExternalInvestmentMutation,
  useUpdateExternalInvestmentMutation,
  useUploadInvestmentLogoMutation,
  useUploadInvestmentPitchDeckMutation,
  useCompleteInvestmentMutation,
  useRemoveInvestmentMutation,
  useRestoreInvestmentMutation,
  useHideInvestmentMutation,
  useUnhideInvestmentMutation,
} = investmentsApi;
